import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  alpha,
} from '@mui/material';
import {
  SwapHoriz as SwapHorizIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

const SectorRotationTracker = ({ momentum }) => {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language;
  
  if (!momentum || (!momentum.rising_sectors?.length && !momentum.falling_sectors?.length)) {
    return null;
  }

  const rising = momentum.rising_sectors || [];
  const falling = momentum.falling_sectors || [];

  // Helper function to get sector name based on current language
  const getSectorName = (sector) => {
    if (currentLang === 'vi' && sector.sector_name_vi) {
      return sector.sector_name_vi;
    }
    return sector.sector_name_en || sector.sector_name || sector.valuation_sector;
  };

  const SectorItem = ({ sector, type, index }) => {
    const isRising = type === 'rising';
    const color = isRising ? '#10b981' : '#ef4444';
    const bgColor = isRising ? '#f0fdf4' : '#fef2f2';

    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          py: 1.5,
          transition: 'all 0.2s',
          '&:hover': {
            bgcolor: bgColor,
            mx: -2,
            px: 2,
            borderRadius: 1,
          },
        }}
      >
        {/* Left: Sector Name */}
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Typography 
            variant="body2" 
            sx={{ 
              fontWeight: 600,
              fontSize: '0.875rem',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {getSectorName(sector)}
          </Typography>
        </Box>

        {/* Rank Change */}
        {sector.rank_change !== 0 && (
          <Typography 
            variant="caption" 
            sx={{ 
              color: color, 
              fontWeight: 600,
              fontSize: '0.75rem',
              minWidth: 24,
              textAlign: 'center',
            }}
          >
            {isRising ? '↑' : '↓'}{Math.abs(sector.rank_change)}
          </Typography>
        )}

        {/* Score Change */}
        <Typography
          variant="body1"
          sx={{
            fontWeight: 700,
            color: color,
            fontSize: '1rem',
            minWidth: 50,
            textAlign: 'right',
          }}
        >
          {sector.score_change > 0 ? '+' : ''}{sector.score_change.toFixed(1)}
        </Typography>
      </Box>
    );
  };

  return (
    <Card 
      elevation={0}
      sx={{ 
        mb: 3,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
      }}
    >
      <CardContent sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 3, mb: 3 }}>
          {/* Title */}
          <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 1.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: alpha('#667eea', 0.1),
                color: '#667eea',
                mr: 2,
              }}
            >
              <SwapHorizIcon sx={{ fontSize: 22 }} />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1rem', mb: 0.25 }}>
                {t('dashboard.sectorRotation.title')}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                {t('dashboard.sectorRotation.subtitle')}
              </Typography>
            </Box>
          </Box>

          {/* Stats */}
          <Box sx={{ display: 'flex', gap: 3 }}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#10b981', lineHeight: 1, mb: 0.5 }}>
                {rising.length}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Tăng
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#ef4444', lineHeight: 1, mb: 0.5 }}>
                {falling.length}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Giảm
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#667eea', lineHeight: 1, mb: 0.5 }}>
                {momentum.total_sectors || rising.length + falling.length}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Tổng
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Content */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 3 }}>
          {/* Rising */}
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, pb: 1.5, borderBottom: '2px solid', borderColor: '#10b981' }}>
              <TrendingUpIcon sx={{ fontSize: 18, color: '#10b981' }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#10b981', fontSize: '0.8125rem' }}>
                {t('dashboard.sectorRotation.moneyFlowingIn')}
              </Typography>
              <Box sx={{ flex: 1 }} />
              <Chip
                label={rising.length}
                size="small"
                sx={{
                  height: 22,
                  bgcolor: '#10b981',
                  color: 'white',
                  fontWeight: 700,
                  fontSize: '0.75rem',
                }}
              />
            </Box>
            
            <Box>
              {rising.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center', fontStyle: 'italic' }}>
                  Không có dữ liệu
                </Typography>
              ) : (
                rising.slice(0, 10).map((sector, idx) => (
                  <SectorItem key={idx} sector={sector} type="rising" index={idx + 1} />
                ))
              )}
            </Box>
          </Box>

          {/* Falling */}
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, pb: 1.5, borderBottom: '2px solid', borderColor: '#ef4444' }}>
              <TrendingDownIcon sx={{ fontSize: 18, color: '#ef4444' }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#ef4444', fontSize: '0.8125rem' }}>
                {t('dashboard.sectorRotation.moneyFlowingOut')}
              </Typography>
              <Box sx={{ flex: 1 }} />
              <Chip
                label={falling.length}
                size="small"
                sx={{
                  height: 22,
                  bgcolor: '#ef4444',
                  color: 'white',
                  fontWeight: 700,
                  fontSize: '0.75rem',
                }}
              />
            </Box>
            
            <Box>
              {falling.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center', fontStyle: 'italic' }}>
                  Không có dữ liệu
                </Typography>
              ) : (
                falling.slice(0, 10).map((sector, idx) => (
                  <SectorItem key={idx} sector={sector} type="falling" index={idx + 1} />
                ))
              )}
            </Box>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default SectorRotationTracker;
